import { defineComponent, PropType, computed, ref, watch, onMounted, inject } from 'vue';
import SSelector from '@/components/stemdo-components/s-selector/SSelector.vue';
import SSearch from '@/components/stemdo-components/s-search/SSearch.vue';
import { useI18N } from '@/plugins/i18n';
import SSelectorMultiple from '@/components/stemdo-components/s-selector-multiple/SSelectorMultiple.vue';
import { useStore } from '@/plugins/vuex';
import { Stemdoer, Stemdoers } from '@/shared/model/stemdoer.model';
import { Company } from '@/shared/model/company.model';
import VerticalService from '@/sections/admin/entities/vertical/vertical.service';

const stemdoerStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PRE_HIRABLE: 'preHirable'
};

export default defineComponent({
  props: {
    stemdoers: {
      type: Array as PropType<Stemdoers[]>,
      required: true
    },
    companyItems: {
      type: Array,
      required: false,
      default: () => []
    },
    projectItems: {
      type: Array,
      required: false,
      default: () => []
    },
    onSearch: {
      type: Function,
      required: true
    },
    onVerticalChange: {
      type: Function,
      required: true
    },
    onStatusChange: {
      type: Function,
      required: true
    },
    onCompanyChange: {
      type: Function,
      required: false
    },
    onProjectChange: {
      type: Function,
      required: false
    }
  },
  components: {
    SSelector,
    SSelectorMultiple,
    SSearch
  },
  setup(props) {
    const verticalService: VerticalService = inject('verticalService');
    const store = useStore();
    const i18n = useI18N();
    const verticalsSelected = ref<string[]>([]);
    const statusSelected = ref<string[]>([]);
    const companiesSelected = ref<string[]>([]);
    const projectsSelected = ref<string[]>([]);
    const storedSearch = ref<string>(undefined);
    const verticalItems = ref([]);
    const verticalsData = ref([]);

    onMounted(() => {
      fetchVerticals();
      const storedFilters = store.getters['pagesStore/manager/stemdoers/filters'];
      if (storedFilters.vertical) verticalsSelected.value = storedFilters.vertical;
      if (storedFilters.status) statusSelected.value = storedFilters.status;
      if (storedFilters.company) companiesSelected.value = storedFilters.company;
      if (storedFilters.project) projectsSelected.value = storedFilters.project;
      storedSearch.value = storedFilters.search;
    });

    watch(
      () => verticalsSelected.value,
      verticals => {
        props.onVerticalChange(verticals);
      }
    );

    watch(
      () => companiesSelected.value,
      companies => {
        if (props.onCompanyChange) props.onCompanyChange(companies);
      }
    );

    watch(
      () => statusSelected.value,
      status => {
        props.onStatusChange(status);
      }
    );

    watch(
      () => projectsSelected.value,
      projects => {
        if (props.onProjectChange) props.onProjectChange(projects);
      }
    );

    const fetchVerticals = async () => {
      try {
        const verticals = await verticalService.retrieve();
        verticalsData.value = verticals;
        updateVerticalItems();
      } catch (error) {
        console.error('Error fetching verticals:', error);
      }
    };

    const updateVerticalItems = () => {
      const verticalsSet = new Set([
        {
          id: 'VIEW_ALL',
          desc: i18n.t('stemdoers-filter-list.vertical.all') as string
        }
      ]);
      verticalsData.value.forEach(vertical => {
        verticalsSet.add({
          id: vertical.id,
          desc: i18n.t(`stemdoers-filter-list.vertical.${vertical.name.toLowerCase().replace(/\s+/g, '')}`) as string
        });
      });
      verticalItems.value = Array.from(verticalsSet);
    };

    watch(() => i18n.locale, updateVerticalItems);

    const statusItems = computed(() => {
      const statusSet = new Set([
        {
          id: 'VIEW_ALL',
          desc: i18n.t('stemdoers-filter-list.status.all') as string
        }
      ]);

      Object.values(stemdoerStatus).forEach(status => {
        let id;
        let translation = '';
        switch (status) {
          case stemdoerStatus.ACTIVE:
            id = stemdoerStatus.ACTIVE;
            translation = i18n.t('stemdoers-filter-list.status.active') as string;
            break;
          case stemdoerStatus.INACTIVE:
            id = stemdoerStatus.INACTIVE;
            translation = i18n.t('stemdoers-filter-list.status.inactive') as string;
            break;
          case stemdoerStatus.PRE_HIRABLE:
            id = stemdoerStatus.PRE_HIRABLE;
            translation = i18n.t('stemdoers-filter-list.status.preHirable') as string;
            break;
        }
        statusSet.add({
          id: id,
          desc: translation
        });
      });
      return Array.from(statusSet);
    });

    return {
      verticalsSelected,
      statusSelected,
      companiesSelected,
      projectsSelected,
      storedSearch,
      verticalItems,
      statusItems
    };
  }
});
